import { AlertTriangle, UserCheck } from 'lucide-react'
import type { Deal, DealStage } from '@/types'
import { cn } from '@/utils/cn'
import { formatDate } from '@/utils/format-date'
import { InfoField } from './InfoField'
import { dealHasBdrHandoff } from './utils'

const HANDOFF_STAGES = new Set<DealStage>(['qualificado', 'reuniao_agendada', 'reuniao_realizada'])
const HANDOFF_GATE_STAGES = new Set<DealStage>(['qualificado', 'reuniao_agendada'])

function getHandoffDores(deal: Deal) {
  return deal.doresIdentificadas?.trim() || deal.leadProblema?.trim() || ''
}

export function HandoffBdrSection({ deal }: { deal: Deal }) {
  if (!HANDOFF_STAGES.has(deal.etapa) || !dealHasBdrHandoff(deal)) return null

  const dores = getHandoffDores(deal)
  const showIncompleteAlert = HANDOFF_GATE_STAGES.has(deal.etapa)
  const missing: string[] = []
  if (showIncompleteAlert) {
    if (!dores) missing.push('Dores / problema identificado')
    if (!deal.telefone && !deal.contatoNome) missing.push('Contato (nome ou telefone)')
    if (!deal.bdrNome) missing.push('BDR responsável')
  }

  return (
    <div
      className={cn(
        'glass-card p-4 space-y-3 border-l-2',
        missing.length > 0 ? 'border-l-warning' : 'border-l-accent/60',
      )}
    >
      <h3 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-2">
        <UserCheck size={14} />
        {deal.etapa === 'reuniao_realizada' ? 'Contexto BDR' : 'Handoff BDR → Closer'}
      </h3>

      {missing.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3">
          <AlertTriangle size={14} className="handoff-alert-icon mt-0.5 shrink-0 text-warning" />
          <div>
            <p className="handoff-alert-title text-xs font-semibold text-warning">Handoff incompleto</p>
            <ul className="mt-1 space-y-0.5">
              {missing.map((item) => (
                <li key={item} className="text-xs font-medium text-text-secondary">
                  • {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <InfoField label="BDR Responsável" value={deal.bdrNome || '—'} />
        <InfoField label="Contato" value={deal.contatoNome || '—'} />
        <InfoField label="Telefone" value={deal.telefone || '—'} />
        <InfoField label="E-mail" value={deal.email || '—'} />
        <InfoField label="Origem" value={deal.leadOrigem || '—'} />
        <InfoField
          label="Entrada na Carteira"
          value={deal.dataEntradaLead ? formatDate(deal.dataEntradaLead) : '—'}
        />
      </div>

      <InfoField label="Dores / Problema" value={dores || '—'} />
      {(deal.budgetConfirmado || deal.decisorPresente) && (
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/[0.06]">
          <InfoField label="Budget Confirmado" value={deal.budgetConfirmado || '—'} />
          <InfoField label="Decisor Presente" value={deal.decisorPresente || '—'} />
        </div>
      )}
    </div>
  )
}
